import { isValidMove, makeMove, isInCheck, isCheckmate } from './chessLogic';

// Piece values for evaluation
const PIECE_VALUES = {
  pawn: 1,
  knight: 3,
  bishop: 3,
  rook: 5,
  queen: 9,
  king: 1000
};

// Position bonus tables for different pieces
const PAWN_TABLE = [
  [0,  0,  0,  0,  0,  0,  0,  0],
  [50, 50, 50, 50, 50, 50, 50, 50],
  [10, 10, 20, 30, 30, 20, 10, 10],
  [5,  5, 10, 25, 25, 10,  5,  5],
  [0,  0,  0, 20, 20,  0,  0,  0],
  [5, -5,-10,  0,  0,-10, -5,  5],
  [5, 10, 10,-20,-20, 10, 10,  5],
  [0,  0,  0,  0,  0,  0,  0,  0]
];

const KNIGHT_TABLE = [
  [-50,-40,-30,-30,-30,-30,-40,-50],
  [-40,-20,  0,  0,  0,  0,-20,-40],
  [-30,  0, 10, 15, 15, 10,  0,-30],
  [-30,  5, 15, 20, 20, 15,  5,-30],
  [-30,  0, 15, 20, 20, 15,  0,-30],
  [-30,  5, 10, 15, 15, 10,  5,-30],
  [-40,-20,  0,  5,  5,  0,-20,-40],
  [-50,-40,-30,-30,-30,-30,-40,-50]
];

const BISHOP_TABLE = [
  [-20,-10,-10,-10,-10,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5, 10, 10,  5,  0,-10],
  [-10,  5,  5, 10, 10,  5,  5,-10],
  [-10,  0, 10, 10, 10, 10,  0,-10],
  [-10, 10, 10, 10, 10, 10, 10,-10],
  [-10,  5,  0,  0,  0,  0,  5,-10],
  [-20,-10,-10,-10,-10,-10,-10,-20]
];

const ROOK_TABLE = [
  [0,  0,  0,  0,  0,  0,  0,  0],
  [5, 10, 10, 10, 10, 10, 10,  5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [0,  0,  0,  5,  5,  0,  0,  0]
];

const QUEEN_TABLE = [
  [-20,-10,-10, -5, -5,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5,  5,  5,  5,  0,-10],
  [-5,  0,  5,  5,  5,  5,  0, -5],
  [0,  0,  5,  5,  5,  5,  0, -5],
  [-10,  5,  5,  5,  5,  5,  0,-10],
  [-10,  0,  5,  0,  0,  0,  0,-10],
  [-20,-10,-10, -5, -5,-10,-10,-20]
];

const KING_TABLE = [
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-20,-30,-30,-40,-40,-30,-30,-20],
  [-10,-20,-20,-20,-20,-20,-20,-10],
  [20, 20,  0,  0,  0,  0, 20, 20],
  [20, 30, 10,  0,  0, 10, 30, 20]
];

// Get position value for a piece
const getPositionValue = (piece, row, col) => {
  if (!piece) return 0;
  
  let table;
  switch (piece.type) {
    case 'pawn': table = PAWN_TABLE; break;
    case 'knight': table = KNIGHT_TABLE; break;
    case 'bishop': table = BISHOP_TABLE; break;
    case 'rook': table = ROOK_TABLE; break;
    case 'queen': table = QUEEN_TABLE; break;
    case 'king': table = KING_TABLE; break;
    default: return 0;
  }
  
  // Flip table for black pieces
  const tableRow = piece.color === 'black' ? row : 7 - row;
  return table[tableRow][col] / 100;
};

// Evaluate the board position
const evaluateBoard = (board) => {
  let score = 0;
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece) {
        const pieceValue = PIECE_VALUES[piece.type];
        const positionValue = getPositionValue(piece, row, col);
        const totalValue = pieceValue + positionValue;
        
        if (piece.color === 'black') {
          score += totalValue;
        } else {
          score -= totalValue;
        }
      }
    }
  }
  
  return score;
};

// Get all possible moves for a color
const getAllMoves = (board, color) => {
  const moves = [];
  
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === color) {
        const validMoves = isValidMove(board, row, col, color);
        for (const move of validMoves) {
          moves.push({
            from: [row, col],
            to: [move.row, move.col],
            piece
          });
        }
      }
    }
  }
  
  return moves;
};

// Minimax algorithm with alpha-beta pruning
const minimax = (board, depth, alpha, beta, isMaximizing, color) => {
  if (depth === 0) {
    return evaluateBoard(board);
  }
  
  const moves = getAllMoves(board, color);
  
  if (moves.length === 0) {
    if (isInCheck(board, color)) {
      // Checkmate
      return isMaximizing ? -10000 + depth : 10000 - depth;
    } else {
      // Stalemate
      return 0;
    }
  }
  
  if (isMaximizing) {
    let maxEvaluation = -Infinity;
    
    for (const move of moves) {
      const moveResult = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], color);
      if (moveResult.success) {
        const evaluation = minimax(moveResult.newBoard, depth - 1, alpha, beta, false, color === 'white' ? 'black' : 'white');
        maxEvaluation = Math.max(maxEvaluation, evaluation);
        alpha = Math.max(alpha, evaluation);
        
        if (beta <= alpha) {
          break; // Alpha-beta pruning
        }
      }
    }
    
    return maxEvaluation;
  } else {
    let minEvaluation = Infinity;
    
    for (const move of moves) {
      const moveResult = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], color);
      if (moveResult.success) {
        const evaluation = minimax(moveResult.newBoard, depth - 1, alpha, beta, true, color === 'white' ? 'black' : 'white');
        minEvaluation = Math.min(minEvaluation, evaluation);
        beta = Math.min(beta, evaluation);
        
        if (beta <= alpha) {
          break; // Alpha-beta pruning
        }
      }
    }
    
    return minEvaluation;
  }
};

// Get the best move for the computer
export const getComputerMove = (board, color = 'black') => {
  const moves = getAllMoves(board, color);
  
  if (moves.length === 0) {
    return null;
  }
  
  let bestMove = null;
  let bestValue = -Infinity;
  const depth = 3; // Adjust depth for difficulty (higher = stronger but slower)
  
  // Shuffle moves to add some randomness
  const shuffledMoves = moves.sort(() => Math.random() - 0.5);
  
  for (const move of shuffledMoves) {
    const moveResult = makeMove(board, move.from[0], move.from[1], move.to[0], move.to[1], color);
    if (moveResult.success) {
      const value = minimax(moveResult.newBoard, depth - 1, -Infinity, Infinity, false, color === 'white' ? 'black' : 'white');
      
      if (value > bestValue) {
        bestValue = value;
        bestMove = move;
      }
    }
  }
  
  return bestMove;
};